/**
 * Shared react-select style overrides tuned for the product-form tokens.
 * Using this everywhere guarantees every dropdown looks and behaves the same.
 */

const cssVar = (name, fallback) => `var(${name}, ${fallback})`;

const base = {
  border: cssVar("--pf-border", "#e4e9ec"),
  borderStrong: cssVar("--pf-border-strong", "#cfd6db"),
  accent: cssVar("--pf-accent", "#4b7a8e"),
  accentSoft: cssVar("--pf-accent-soft", "#eef4f7"),
  accentStrong: cssVar("--pf-accent-strong", "#25343f"),
  danger: cssVar("--pf-danger", "#d14343"),
  surface: cssVar("--pf-surface", "#ffffff"),
  surfaceMuted: cssVar("--pf-surface-muted", "#f7f9f9"),
  text: cssVar("--pf-text", "#25343f"),
  textMuted: cssVar("--pf-text-muted", "#8a9ba8"),
  radius: cssVar("--pf-radius-sm", "8px"),
};

export const createSelectStyles = ({ invalid = false } = {}) => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: 36,
    backgroundColor: state.isDisabled ? base.surfaceMuted : base.surface,
    borderRadius: base.radius,
    borderColor: invalid
      ? base.danger
      : state.isFocused
      ? base.accent
      : base.border,
    boxShadow: state.isFocused
      ? invalid
        ? "0 0 0 3px rgba(209, 67, 67, 0.12)"
        : "0 0 0 3px rgba(75, 122, 142, 0.18)"
      : "none",
    "&:hover": {
      borderColor: invalid ? base.danger : base.borderStrong,
    },
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 8px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: base.textMuted,
    fontSize: 14,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: base.text,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: base.accentSoft,
    borderRadius: 4,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: base.accentStrong,
    fontSize: 12,
    padding: "2px 6px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: base.accentStrong,
    ":hover": {
      backgroundColor: base.accent,
      color: "#fff",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: base.radius,
    boxShadow:
      "0 10px 30px rgba(16, 24, 40, 0.08), 0 0 0 1px rgba(16, 24, 40, 0.04)",
    overflow: "hidden",
    zIndex: 30,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "8px 12px",
    fontSize: 14,
    color: state.isSelected ? base.accentStrong : base.text,
    backgroundColor: state.isSelected
      ? base.accentSoft
      : state.isFocused
      ? base.surfaceMuted
      : "transparent",
    cursor: "pointer",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? base.accent : base.textMuted,
    padding: "0 8px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  clearIndicator: (provided) => ({
    ...provided,
    color: base.textMuted,
    padding: "0 4px",
  }),
});

export const selectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: "#4b7a8e",
    primary25: "#eef4f7",
    primary50: "#dfeaee",
    primary75: "#c3d5dc",
  },
});
